import { useEffect, useState, useCallback } from "react";
import { apiClient } from "../App";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { CreditCard, CheckCircle2, AlertTriangle, Loader2, ExternalLink, RefreshCw } from "lucide-react";
import { useFeatureMode, isFinancialExecutionAllowed } from "../hooks/useFeatureMode";
import SafetyFreezeNotice from "./SafetyFreezeNotice";

const STATE = {
  payment_ready: { label: "Connected & live", tone: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2 },
  payouts_disabled: { label: "Payouts disabled", tone: "bg-amber-100 text-amber-700", Icon: AlertTriangle },
  bacs_not_ready: { label: "Direct Debit not ready", tone: "bg-amber-100 text-amber-700", Icon: AlertTriangle },
  charges_disabled: { label: "Charges disabled", tone: "bg-rose-100 text-rose-700", Icon: AlertTriangle },
  requirements_due: { label: "Stripe action needed", tone: "bg-amber-100 text-amber-700", Icon: AlertTriangle },
  connected_onboarding_incomplete: { label: "Onboarding incomplete", tone: "bg-amber-100 text-amber-700", Icon: Loader2 },
  unknown_refresh_required: { label: "Not verified — refresh required", tone: "bg-amber-100 text-amber-700", Icon: RefreshCw },
  not_connected: { label: "Not connected", tone: "bg-slate-100 text-slate-600", Icon: AlertTriangle },
};

// OCI V2.20 — Charity-facing Stripe payment setup. Wired to the canonical bridge:
// /charity-portal/payment-setup + /charity-portal/connect/start|status.
export default function CharityPaymentsPanel() {
  const featureMode = useFeatureMode();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get("/charity-portal/payment-setup");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load payment status");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Re-check after returning from Stripe onboarding
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("charity_connect")) {
      apiClient.get("/charity-portal/connect/status").catch(() => {}).finally(load);
    }
  }, [load]);

  const connect = async () => {
    setConnecting(true);
    try {
      const r = await apiClient.post("/charity-portal/connect/start", { origin_url: window.location.origin });
      if (r.data?.onboardingUrl) {
        window.location.href = r.data.onboardingUrl;
      } else {
        toast.error("No onboarding URL returned");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      const isHtmlOr5xx = !detail || /<!DOCTYPE|<html|Cloudflare/i.test(String(detail)) || [502, 503, 504].includes(e?.response?.status);
      if (isHtmlOr5xx || detail.includes("Invalid API Key") || detail.includes("Stripe key not configured")) {
        toast.error("Stripe isn't fully configured on the platform yet. Please contact your platform administrator.");
      } else {
        toast.error(detail || "Could not start Stripe onboarding");
      }
    } finally {
      setConnecting(false);
    }
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-slate-400 text-sm py-10 justify-center" data-testid="charity-payments-loading"><Loader2 className="h-5 w-5 animate-spin" /> Loading payment status…</div>;
  }
  if (!data) return null;

  // Package 0 Safety Freeze — hide Stripe controls while financial execution is disabled.
  if (!isFinancialExecutionAllowed(featureMode)) {
    return (
      <div className="max-w-2xl" data-testid="charity-payments-panel-disabled">
        <SafetyFreezeNotice
          title="Charity payment setup is not part of the current release"
          message="Charity payment onboarding is not part of the current release. Donor submissions are recorded securely for the charity regardless."
          testId="charity-payments-safety-notice"
        />
      </div>
    );
  }

  const s = STATE[data.connect?.state] || STATE.not_connected;
  const blockers = data.connect?.blockers || [];
  const isLive = data.connect?.state === "payment_ready";
  const lastSynced = data.connect?.lastSyncedAt;

  // Refresh pulls a fresh Stripe status sync (updates stored readiness flags), then reloads.
  const refreshStatus = async () => {
    try {
      await apiClient.get("/charity-portal/connect/status");
    } catch (e) { /* status sync best-effort */ }
    load();
  };

  return (
    <div className="max-w-2xl space-y-4" data-testid="charity-payments-panel">
      <Card className="border-slate-200">
        <CardContent className="p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-indigo-600 flex items-center justify-center">
                <CreditCard className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-slate-900 text-lg leading-tight">Stripe payment destination</h2>
                <p className="text-sm text-slate-500">{data.charityName}</p>
              </div>
            </div>
            <Badge className={`${s.tone} hover:${s.tone} gap-1.5`} data-testid="charity-connect-state" data-state={data.connect?.state}>
              <s.Icon className={`h-3.5 w-3.5 ${data.connect?.state === "connected_onboarding_incomplete" ? "animate-spin" : ""}`} />
              {s.label}
            </Badge>
          </div>

          {blockers.length > 0 && (
            <ul className="mt-4 space-y-1.5" data-testid="charity-connect-blockers">
              {blockers.map((b, i) => (
                <li key={i} className="text-sm text-amber-700 flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> {b}
                </li>
              ))}
            </ul>
          )}

          {data.connect?.stripeAccountId && (
            <p className="mt-3 text-xs text-slate-400">Connected account: <span className="font-mono">{data.connect.stripeAccountId}</span></p>
          )}
          {lastSynced && (
            <p className="mt-1 text-xs text-slate-400" data-testid="charity-connect-last-synced">
              Stripe status last checked: {new Date(lastSynced).toLocaleString("en-GB")}
            </p>
          )}

          <div className="mt-5 flex items-center gap-3">
            <Button onClick={connect} disabled={connecting} data-testid="charity-connect-stripe-btn"
                    className="bg-indigo-600 hover:bg-indigo-700 text-white gap-2">
              {connecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
              {isLive ? "Manage / Reconnect Stripe" : data.connect?.state === "not_connected" ? "Connect Stripe" : "Continue Stripe onboarding"}
            </Button>
            <Button variant="ghost" size="sm" onClick={refreshStatus} data-testid="charity-connect-refresh" className="gap-1.5 text-slate-500">
              <RefreshCw className="h-4 w-4" /> Refresh Stripe status
            </Button>
          </div>

          <p className="mt-4 text-xs text-slate-400 leading-relaxed">
            Connecting Stripe lets donations on campaigns where your charity is the beneficiary settle directly to your own Stripe account.
            We never ask for secret API keys — onboarding is handled securely by Stripe.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
